/**
 * Supabase Integration
 *
 * Re-exports all Supabase-related modules for easy importing.
 */

// Client
export { supabase, isSupabaseConfigured } from './client';

// Types
export type {
  Database,
  Profile,
  StartupProfile,
  InvestorProfile,
  Score,
  Conversation,
  Message,
  Dealflow,
  DataRoomDocument,
  DataRoomAccessRequest,
  UserRole,
  Json,
} from './types';

// Auth
export { AuthProvider, useAuth, useIsAuthenticated, useRequireAuth } from './auth';

// API Hooks
export {
  queryKeys,
  useInvestorProfile,
  useUpsertInvestorProfile,
  useScore,
  useUpsertScore,
  useConversations,
  useMessages,
  useSendMessage,
  useCreateConversation,
  useDealflow,
  useUpdateDealflowStatus,
  useSupabaseStatus,
  // Data Room hooks
  useDataRoomDocuments,
  useAddDataRoomDocument,
  useDeleteDataRoomDocument,
  useDataRoomAccessRequests,
  useRequestDataRoomAccessNew,
  useRespondToAccessRequest,
} from './api';

// Messaging row → domain-type mappers
export {
  mapConversationRow,
  mapMessageRow,
  isRelationshipDenied,
} from './messaging-mappers';

// Realtime Hooks
export {
  useRealtimeMessages,
  useRealtimeConversations,
  useTypingIndicator,
  usePresence,
  useRealtimeNotifications,
  useDemoRealtimeMessages,
  markMessagesAsRead,
} from './realtime';

export type {
  TypingUser,
  PresenceState,
  RealtimeNotification,
} from './realtime';

// Edge Functions
export {
  callEdgeFunction,
  // Stripe
  createCheckoutSession,
  createPortalSession,
  // Team
  sendTeamInvite,
  acceptTeamInvite,
  // DD
  sendDDInvite,
  acceptDDInvite,
  // Documents
  getDocumentSignedUrl,
  // AI
  extractPitchDeck,
  getInvestorMatches,
  // Claim Submission
  validateClaimToken,
  claimSubmission,
  // Rescore & Visibility
  rescoreSubmission,
  toggleSubmissionVisibility,
} from './edge-functions';

export type {
  EdgeFunctionResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  CreatePortalRequest,
  CreatePortalResponse,
  TeamInviteRequest,
  TeamInviteResponse,
  AcceptTeamInviteRequest,
  AcceptTeamInviteResponse,
  DDInviteRequest,
  DDInviteResponse,
  AcceptDDInviteRequest,
  AcceptDDInviteResponse,
  SignedUrlRequest,
  SignedUrlResponse,
  PitchDeckExtractRequest,
  PitchDeckExtractResponse,
  ExtractedField,
  ExtractedStartupData,
  InvestorMatchRequest,
  InvestorMatchResponse,
  InvestorMatch,
  MatchFactors,
  // Claim Submission
  ValidateClaimRequest,
  ValidateClaimResponse,
  ClaimSubmissionRequest,
  ClaimSubmissionResponse,
  // Rescore & Visibility
  RescoreSubmissionRequest,
  RescoreSubmissionResponse,
  ToggleVisibilityRequest,
  ToggleVisibilityResponse,
} from './edge-functions';
